import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "benchmarkEmptyForm",
    "benchmarkSelectedForm",
    "yearWarning",
    "regionWarning",
    "selectBenchmarkPanel",
    "benchmarkIdField",
    "benchmarkRegion",
    "benchmarkMicroRegion",
    "benchmarkYear",
    "benchmarkAvailability",
    "savedBenchmarkAvailability",
    "benchmarkMethodology",
    "benchmarkLocalValueInput",
    "benchmarkUsdValueInput",
    "ankerLink",
    "livingWageLink",
    "wageIndicatorLink",
    "wageIndicatorForSaleLink",
    "fairWageLink",
    "givenLink",
    "givenAnkerLink",
    "additionalReference1",
    "additionalReference2",
    "additionalReferenceValue1",
    "additionalReferenceValue2",
    "nextButton",
  ];

  static values = {
    reportingYear: String,
    facilityRegion: String,
    facilityRegionName: String,
  };

  connect() {
    this.validateForm();

    this.element.addEventListener(
      "benchmark:selected",
      this.handleBenchmarkSelected
    );
  }

  openBenchmarkSelectPanel() {
    this.selectBenchmarkPanelTarget.style.right = "0";
    this.selectBenchmarkPanelTarget.classList.add("panel-open");
    const backgroundOverlay = document.getElementById("background-overlay");
    backgroundOverlay.classList.add("visible");
  }

  closeBenchmarkSelectPanel() {
    this.selectBenchmarkPanelTarget.style.right = "-100%";
    this.selectBenchmarkPanelTarget.classList.remove("panel-open");
    this.selectBenchmarkPanelTarget.style.left = "100%";
    const backgroundOverlay = document.getElementById("background-overlay");
    backgroundOverlay.classList.remove("visible");
  }

  closeYearWarning() {
    this.yearWarningTarget.classList.add("hidden");
  }

  closeRegionWarning() {
    this.regionWarningTarget.classList.add("hidden");
  }

  // Check if benchmark region matches facility region by ISO code or name
  checkRegionsMatch(benchmarkIsoRegion, benchmarkRegionName) {
    // Country-wide benchmarks apply to all regions, so never show warning
    if (benchmarkIsoRegion === "COUNTRY_WIDE") {
      return true;
    }

    // Strategy 1: Check ISO region match
    if (benchmarkIsoRegion && this.facilityRegionValue) {
      if (benchmarkIsoRegion === this.facilityRegionValue) {
        return true;
      }
    }

    // Strategy 2: Check region name match (case-insensitive)
    if (benchmarkRegionName && this.facilityRegionNameValue) {
      const benchmarkNameLower = benchmarkRegionName.toLowerCase();
      const facilityNameLower = this.facilityRegionNameValue.toLowerCase();

      // Exact match
      if (benchmarkNameLower === facilityNameLower) {
        return true;
      }

      // Partial match (one contains the other)
      if (
        benchmarkNameLower.includes(facilityNameLower) ||
        facilityNameLower.includes(benchmarkNameLower)
      ) {
        return true;
      }
    }

    // If no facility region is set, don't show warning
    if (!this.facilityRegionValue && !this.facilityRegionNameValue) {
      return true;
    }

    // If benchmark has no region info, don't show warning
    if (!benchmarkIsoRegion && !benchmarkRegionName) {
      return true;
    }

    return false;
  }

  methodologyName = (methodology) => {
    switch (methodology) {
      case "anker":
        return "Full-Fledged Anker Methodology";
      case "living-wage-us":
        return "Living Wage US Methodology";
      case "wage-indicator":
        return "WageIndicator Typical Family Methodology";
      case "fair-wage":
        return "Fair Wage Network Typical Family Methodology";
      case "other":
        return "Other";
      default:
        return "Other";
    }
  };

  handleBenchmarkSelected = (event) => {
    const benchmarkId = event.detail.benchmark.id;
    const benchmarkMethodology = event.detail.benchmark.methodology;
    const benchmarkRegion =
      event.detail.benchmark.country_region ||
      event.detail.benchmark.iso_region_name;
    const benchmarkIsoRegion = event.detail.benchmark.iso_region;
    const benchmarkLink = event.detail.benchmark.link;
    const benchmarkMicroRegion = event.detail.benchmark.micro_region;
    const benchmarkYear = event.detail.benchmark.year;
    const benchmarkAvailability = event.detail.benchmark.availability;
    const benchmarkGrossLocal = event.detail.benchmark.gross_local;
    const benchmarkGrossUsd = event.detail.benchmark.gross_usd;

    if (
      this.hasBenchmarkIdFieldTarget &&
      this.benchmarkIdFieldTarget.value == benchmarkId
    ) {
      // Same benchmark selected
      this.closeBenchmarkSelectPanel();
      return;
    }

    // update Public or For Purchase
    this.updateBenchmarkAvailability(benchmarkAvailability);

    if (this.hasBenchmarkIdFieldTarget) {
      this.benchmarkIdFieldTarget.value = benchmarkId;
    }

    this.closeBenchmarkSelectPanel();
    if (this.hasBenchmarkEmptyFormTarget) {
      this.benchmarkEmptyFormTarget.hidden = true;
    }
    this.benchmarkSelectedFormTarget.hidden = false;

    if (benchmarkYear !== this.reportingYearValue) {
      this.yearWarningTarget.classList.remove("hidden");
    } else {
      this.yearWarningTarget.classList.add("hidden");
    }

    // Compare regions - check both ISO code and region name
    const regionsMatch = this.checkRegionsMatch(
      benchmarkIsoRegion,
      benchmarkRegion
    );
    if (!regionsMatch) {
      this.regionWarningTarget.classList.remove("hidden");
    } else {
      this.regionWarningTarget.classList.add("hidden");
    }

    if (this.hasBenchmarkLocalValueInputTarget) {
      this.benchmarkLocalValueInputTarget.value = benchmarkGrossLocal;
    }

    if (this.hasBenchmarkUsdValueInputTarget) {
      this.benchmarkUsdValueInputTarget.value = benchmarkGrossUsd;
    }

    if (this.hasAdditionalReference1Target) {
      this.additionalReference1Target.value = "";
    }

    if (this.hasAdditionalReference2Target) {
      this.additionalReference2Target.value = "";
    }

    if (this.hasAdditionalReferenceValue1Target) {
      this.additionalReferenceValue1Target.value = "";
    }

    if (this.hasAdditionalReferenceValue2Target) {
      this.additionalReferenceValue2Target.value = "";
    }

    if (this.hasBenchmarkRegionTarget) {
      this.benchmarkRegionTarget.textContent = "";
      this.benchmarkRegionTarget.textContent = benchmarkRegion;
    }

    if (this.hasGivenAnkerLinkTarget) {
      this.givenAnkerLinkTarget.hidden = true;
    }

    const benchmarkAvailabilityLink = event.detail.benchmark.availability_link;

    if (this.hasAnkerLinkTarget && benchmarkMethodology == "anker" && !benchmarkAvailabilityLink) {
      this.ankerLinkTarget.href = "";
      this.ankerLinkTarget.href = benchmarkLink;
      this.ankerLinkTarget.hidden = false;
    } else if (this.hasAnkerLinkTarget) {
      this.ankerLinkTarget.hidden = true;
    }

    if (this.hasBenchmarkMicroRegionTarget) {
      this.benchmarkMicroRegionTarget.textContent = "";
      this.benchmarkMicroRegionTarget.textContent = benchmarkMicroRegion;
    }

    if (this.hasBenchmarkYearTarget) {
      this.benchmarkYearTarget.textContent = "";
      this.benchmarkYearTarget.textContent = benchmarkYear;
    }

    if (this.hasBenchmarkMethodologyTarget) {
      this.benchmarkMethodologyTarget.textContent = "";
      this.benchmarkMethodologyTarget.textContent =
        this.methodologyName(benchmarkMethodology);
    }

    this.showMethodologyLink(benchmarkMethodology, benchmarkAvailabilityLink);

    // Validate form after setting all values to enable/disable the next button
    this.validateForm();
  };

  showMethodologyLink(benchmarkMethodology, availabilityLink) {
    if (this.hasGivenLinkTarget) {
      this.givenLinkTarget.hidden = true;
    }

    this.livingWageLinkTarget.hidden = true;
    this.wageIndicatorLinkTarget.hidden = true;
    if (this.hasWageIndicatorForSaleLinkTarget) {
      this.wageIndicatorForSaleLinkTarget.hidden = true;
    }
    this.fairWageLinkTarget.hidden = true;

    if (availabilityLink) {
      if (this.hasWageIndicatorForSaleLinkTarget) {
        this.wageIndicatorForSaleLinkTarget.hidden = false;
      }
      return;
    }

    switch (benchmarkMethodology) {
      case "living-wage-us":
        this.livingWageLinkTarget.hidden = false;
        break;
      case "wage-indicator":
        this.wageIndicatorLinkTarget.hidden = false;
        break;
      case "fair-wage":
        this.fairWageLinkTarget.hidden = false;
        break;
    }
  }

  updateBenchmarkAvailability(availability) {
    if (!this.hasBenchmarkAvailabilityTarget) return;

    if (this.hasSavedBenchmarkAvailabilityTarget) {
      this.savedBenchmarkAvailabilityTarget.classList.add("hidden");
    }

    const spans = this.benchmarkAvailabilityTarget.querySelectorAll(
      "[data-availability]"
    );

    spans.forEach((span) => {
      span.classList.add("hidden");
    });

    const activeSpan = this.benchmarkAvailabilityTarget.querySelector(
      `[data-availability="${availability}"]`
    );

    if (activeSpan) {
      activeSpan.classList.remove("hidden");
    }
  }

  validateForm() {
    const benchmarkSelected = this.benchmarkIdFieldTarget?.value.trim() !== "";
    let value = "";

    if (this.hasBenchmarkLocalValueInputTarget) {
      value = this.benchmarkLocalValueInputTarget.value.trim();
    }
    if (this.hasBenchmarkUsdValueInputTarget) {
      value = this.benchmarkUsdValueInputTarget.value.trim();
    }

    const valueValid = value === "" || Number(value) > 0;

    if (benchmarkSelected && valueValid) {
      this.enableNextButton();
    } else {
      this.disableNextButton();
    }
  }

  benchmarkValueChanged(event) {
    if (Number(event.target.value) <= 0 && event.target.value !== "") {
      event.target.setCustomValidity("Value must be greater than 0");
    } else {
      event.target.setCustomValidity("");
    }
    this.validateForm();
  }

  enableNextButton() {
    this.nextButtonTarget.disabled = false;
  }

  disableNextButton() {
    this.nextButtonTarget.disabled = true;
  }
}
